import Application from "../../Application";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import { IAttachment } from "../../caseLawDocuments/model/Attachment";
import { ICaseLawDocument } from "../../caseLawDocuments/model/CaseLawDocument";

import AttachmentPresentationModel, {
  TAttachmentPresentationModel
} from "../../caseLawDocuments/presenter/model/AttachmentPresentationModel";
import CaseLawDocumentPresentationModel, {
  TCaseLawDocumentPresentationModel
} from "../../caseLawDocuments/presenter/model/CaseLawDocumentPresentationModel";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import {
  IDocumentType,
  TDocumentType
} from "../../documentTypes/model/DocumentType";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisation } from "../../organisations/model/Organisation";
import { IOrganisationIndex } from "../../organisations/model/OrganisationIndex";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "../../organisations/presenter/model/AttachmentTypePresentationModel";
import OrganisationIndexPresentationModel from "../../organisations/presenter/model/OrganisationIndexPresentationModel";
import { IUser } from "../../user/model/User";

export interface TPortalCaseLawDocumentDetailsPresenter
  extends TLoadingAwarePresenter {
  caseLawDocument: TCaseLawDocumentPresentationModel;
  organisations: IOrganisation[];
  caseLawDocumentTypes: TDocumentType[];
  caseLawDocumentIndexes: IOrganisationIndex[];
  attachmentTypes: TAttachmentTypePresentationModel[];
  attachmentContentTypes: IGenericCodebookItem[];
  caseLawDocumentAttachments: TAttachmentPresentationModel[];
  uploadedFile: any;
  indexHierarchyList: { id: number; path: string[] }[];
  user?: IUser;
  invalidatedCaseLawDocuments: TCaseLawDocumentPresentationModel[];
}

export interface IPortalCaseLawDocumentDetailsPresenter
  extends TPortalCaseLawDocumentDetailsPresenter,
    TPresentable {
  loadCaseLawDocument(caseLawDocuments: ICaseLawDocument): void;
  loadReferenceValues(
    caseLawDocumentTypes: IDocumentType[],
    organisations: IOrganisation[],
    attachmentTypes: IAttachmentType[],
    attachmentContentTypes: IGenericCodebookItem[],
    invalidatedCaseLawDocuments: ICaseLawDocument[]
  ): void;
  loadAttachments(caseLawDocumentAttachments: IAttachment[]): void;
  loadCaseLawDocumentIndexes(caseLawDocumentIndexes: IOrganisationIndex[]);
  showCaseLawDocumentDetailsPage(id: number): void;
}

const defaultState: TPortalCaseLawDocumentDetailsPresenter = {
  caseLawDocument: {} as TCaseLawDocumentPresentationModel,
  organisations: [],
  caseLawDocumentTypes: [],
  caseLawDocumentIndexes: [],
  attachmentTypes: [],
  attachmentContentTypes: [],
  caseLawDocumentAttachments: [],
  uploadedFile: undefined,
  indexHierarchyList: [],
  invalidatedCaseLawDocuments: []
};

const PortalCaseLawDocumentDetailsPresenter = withStore<
  IPortalCaseLawDocumentDetailsPresenter,
  TPortalCaseLawDocumentDetailsPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IPortalCaseLawDocumentDetailsPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TPortalCaseLawDocumentDetailsPresenter>();
    loader.start("caseLawDocumentLoader");
    const loadCaseLawDocument = (item: ICaseLawDocument) => {
      const { organisations, caseLawDocumentTypes } =
        _store.getState<TPortalCaseLawDocumentDetailsPresenter>();

      const caseLawDocumentType = caseLawDocumentTypes?.find(
        (value) => value.id === item.documentTypeId
      );
      const organisation = organisations?.find(
        (value) => value.id === item.organisationId
      );
      const selectedLanguage = _store.getState<any>().selectedLanguage;
      const summary = item.summaryTranslations.find(
        (item) => item.languageId === selectedLanguage.id
      )?.summaryTranslation;
      const caseLawDocument = CaseLawDocumentPresentationModel(
        item,
        caseLawDocumentType,
        organisation,
        summary
      );
      _store.update({
        caseLawDocument
      });
      loader.stop("caseLawDocumentLoader");
    };
    const loadReferenceValues = (
      caseLawDocumentTypes: IDocumentType[],
      organisations: IOrganisation[],
      attachmentTypesList: IAttachmentType[],
      attachmentContentTypes: IGenericCodebookItem[],

      invalidatedCaseLawDocumentList: ICaseLawDocument[]
    ) => {
      let attachmentTypes = (attachmentTypesList || []).map((item) => {
        const contentType = attachmentContentTypes?.find(
          (value) => value.id === item.attachmentContentTypeId
        );
        return AttachmentTypePresentationModel(item, contentType);
      });
      const invalidatedCaseLawDocuments = invalidatedCaseLawDocumentList.map(
        (item) => {
          const caseLawDocumentType = caseLawDocumentTypes?.find(
            (value) => value.id === item.documentTypeId
          );
          const organisation = organisations?.find(
            (value) => value.id === item.organisationId
          );
          const selectedLanguage = _store.getState<any>().selectedLanguage;
          const summary = item.summaryTranslations.find(
            (item) => item.languageId === selectedLanguage.id
          )?.summaryTranslation;
          return CaseLawDocumentPresentationModel(
            item,
            caseLawDocumentType,
            organisation,
            summary
          );
        }
      );
      const user = _store.getState<any>().user;

      _store.update({
        caseLawDocumentTypes,
        organisations,
        attachmentContentTypes,
        attachmentTypes,
        user,
        invalidatedCaseLawDocuments
      });
    };

    const loadCaseLawDocumentIndexes = (
      caseLawDocumentIndexes: IOrganisationIndex[]
    ) => {
      const { selectedLanguage } = _store.getState<any>();
      _store.update({
        caseLawDocumentIndexes: caseLawDocumentIndexes.map((item) =>
          OrganisationIndexPresentationModel(item, selectedLanguage)
        )
      });
      prepareHierarchyList();
    };
    const getCorrectIndexTranslation = (index: any) => {
      const { selectedLanguage } = store.getState<any>();
      const nameTranslation = index.nameTranslations.find(
        (item) => item.languageId === selectedLanguage?.id
      )?.nameTranslation;
      return nameTranslation ?? index.name;
    };

    const getCorrectGroupTranslation = (data?: any) => {
      const { selectedLanguage } = store.getState<any>();
      if (data) {
        const nameTranslation = data.nameTranslations.find(
          (item) => item.languageId === selectedLanguage?.id
        )?.nameTranslation;
        return nameTranslation ?? data.name;
      } else {
        return "";
      }
    };
    const findParent = (group, groupList, indexHierarchyList) => {
      if (group?.parentId) {
        let g = groupList.find((g) => g.id === group?.parentId);
        if (g) {
          indexHierarchyList.push(getCorrectGroupTranslation(g));
          return findParent(g, groupList, indexHierarchyList);
        }
      } else if (group?.organisationIndexCategoryId) {
        indexHierarchyList.push(group?.categoryName);
      }
      return indexHierarchyList;
    };

    const prepareHierarchyList = () => {
      const { caseLawDocumentIndexes } =
        _store.getState<TPortalCaseLawDocumentDetailsPresenter>();
      let result = caseLawDocumentIndexes.map((index) => {
        let _indexHierarchyList: string[] = [];
        let firstGroup = index.groups.find(
          (g) => g.id === index.organisationGroupId
        );
        _indexHierarchyList.push(getCorrectIndexTranslation(index));
        _indexHierarchyList.push(getCorrectGroupTranslation(firstGroup));
        _indexHierarchyList = [
          ...findParent(firstGroup, index.groups, _indexHierarchyList)
        ];
        return {
          id: index.id,
          path: _indexHierarchyList
        };
      });
      _store.update({ indexHierarchyList: result });
    };
    loader.start("attachmentLoader");
    const loadAttachments = (attachments: IAttachment[]) => {
      const { attachmentTypes } =
        _store.getState<TPortalCaseLawDocumentDetailsPresenter>();

      let caseLawDocumentAttachments = (attachments || []).map((item) => {
        const attachmentType = attachmentTypes?.find(
          (value) => value.id === item.attachmentTypeId
        );
        return AttachmentPresentationModel(item, attachmentType);
      });
      _store.update({
        caseLawDocumentAttachments
      });
      loader.stop("attachmentLoader");
    };
    const showCaseLawDocumentDetailsPage = (id: number) => {
      return _application.navigator.push({
        pathname: `/case-law-documents/${id.toString()}/details`
      });
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadCaseLawDocument,
      loadReferenceValues,
      loadCaseLawDocumentIndexes,
      loadAttachments,

      showCaseLawDocumentDetailsPage
    };
  },
  defaultState
);

export default PortalCaseLawDocumentDetailsPresenter;
